import { useState, useEffect } from "react";
import { m, AnimatePresence } from "framer-motion";
import { Globe, X } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  detectBrowserPreferences,
  isCurrencyDetectionDismissed,
  dismissCurrencyDetection,
} from "@/lib/detectLocale";

export function CurrencyDetectionBanner() {
  const { user, refreshUser } = useAuth();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [detected, setDetected] = useState<ReturnType<typeof detectBrowserPreferences> | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (!user || isCurrencyDetectionDismissed()) return;

    const prefs = detectBrowserPreferences();
    // Only show if detected currency differs from user's current currency
    if (prefs.currency !== user.currency) {
      setDetected(prefs);
      setVisible(true);
    }
  }, [user]);

  const handleUpdate = async () => {
    if (!detected) return;
    setUpdating(true);
    try {
      await api.patch("/users/me/preferences", {
        currency: detected.currency,
        locale: detected.locale,
        preferredMarketplace: detected.marketplace,
      });
      await refreshUser();
      dismissCurrencyDetection();
      setVisible(false);
    } catch {
      // Silently fail — user can always change manually in settings
      dismissCurrencyDetection();
      setVisible(false);
    } finally {
      setUpdating(false);
    }
  };

  const handleDismiss = () => {
    dismissCurrencyDetection();
    setVisible(false);
  };

  if (!detected) return null;

  const detectedLabel = t(`currency.${detected.currency.toLowerCase()}` as any) || detected.currency;
  const currentLabel = t(`currency.${(user?.currency || "AUD").toLowerCase()}` as any) || user?.currency;

  return (
    <AnimatePresence>
      {visible && (
        <m.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-lg"
        >
          <div className="rounded-xl border border-white/10 bg-card/95 backdrop-blur-xl shadow-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-purple-500/10 p-2 shrink-0">
                <Globe className="h-5 w-5 text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-foreground">
                  {t("currency.updateCurrency")}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  {t("currency.detectedCurrency", {
                    currency: detectedLabel,
                    current: currentLabel,
                  })}
                </p>
                <div className="mt-3 flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="gradient"
                    onClick={handleUpdate}
                    disabled={updating}
                  >
                    {updating
                      ? t("currency.updating")
                      : t("currency.switchTo", { currency: detected.currency })}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleDismiss}
                    className="text-muted-foreground"
                  >
                    {t("currency.keep", { currency: user?.currency || "AUD" })}
                  </Button>
                </div>
              </div>
              <button
                onClick={handleDismiss}
                className="shrink-0 rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  );
}
